import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { priceBookAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Loader2, Save, Plus, Trash2, DollarSign, Tag,
  Package, Link2, Layers, Calculator, Clock, Camera, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { ImageThumbnail } from '../components/ImageThumbnail';
import { InventoryModeEditor } from '../components/InventoryModeEditor';

const TYPE_COLORS = {
  service: 'bg-blue-100 text-blue-700',
  material: 'bg-amber-100 text-amber-700',
  labor: 'bg-purple-100 text-purple-700',
  bundle: 'bg-green-100 text-green-700',
};

export default function PriceBookItemEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaultLaborRate, setDefaultLaborRate] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  // Component add form
  const [newComp, setNewComp] = useState(null);
  // Search PB items for bundle component refs
  const [compSearchResults, setCompSearchResults] = useState([]);
  const [compSearchQuery, setCompSearchQuery] = useState('');

  const fetchItem = useCallback(async () => {
    try {
      const res = await priceBookAPI.getItem(id);
      setItem(res.data);
    } catch {
      toast.error('Item not found');
      navigate('/price-book');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await priceBookAPI.listCategories();
      setCategories(res.data.categories || []);
    } catch { /* ignore */ }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await priceBookAPI.getSettings();
      setDefaultLaborRate(res.data.default_labor_rate);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchItem(); fetchCategories(); fetchSettings(); }, [fetchItem, fetchCategories, fetchSettings]);

  const updateField = async (field, value) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await priceBookAPI.updateItem(id, { [field]: value });
      setItem(res.data);
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const updateMultiple = async (data) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await priceBookAPI.updateItem(id, data);
      setItem(res.data);
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const res = await priceBookAPI.uploadItemImage(id, file);
      setItem(prev => ({ ...prev, image_url: res.data.image_url, image_thumb_url: res.data.image_thumb_url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleImageDelete = async () => {
    try {
      await priceBookAPI.deleteItemImage(id);
      setItem(prev => ({ ...prev, image_url: null, image_thumb_url: null }));
      toast.success('Image removed');
    } catch {
      toast.error('Failed to remove image');
    }
  };

  // Component CRUD
  const handleAddComponent = async () => {
    if (!newComp) return;
    try {
      const res = await priceBookAPI.addComponent(id, newComp);
      setItem(res.data);
      setNewComp(null);
      toast.success('Component added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add component');
    }
  };

  const handleDeleteComponent = async (compId) => {
    try {
      const res = await priceBookAPI.deleteComponent(compId);
      setItem(res.data);
    } catch {
      toast.error('Failed to remove component');
    }
  };

  const searchPBItems = async (q) => {
    setCompSearchQuery(q);
    if (q.length < 2) { setCompSearchResults([]); return; }
    try {
      const res = await priceBookAPI.listItems({ search: q, page_size: 10 });
      setCompSearchResults((res.data.items || []).filter(i => i.id !== id && i.pricing_mode !== 'bundle'));
    } catch { setCompSearchResults([]); }
  };

  if (loading) {
    return <Layout><div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></Layout>;
  }
  if (!item) return null;

  const isBundle = item.pricing_mode === 'bundle';

  return (
    <Layout>
      <div className="space-y-6" data-testid="pb-item-editor">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate('/price-book')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <ImageThumbnail
            src={item.image_thumb_url || item.image_url}
            fullSrc={item.image_url}
            alt={item.name}
            size="lg"
            fallbackIcon={item.item_type === 'material' ? Package : undefined}
            data-testid="pb-item-header-thumb"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold truncate" data-testid="pb-item-name">{item.name}</h1>
              <Badge className={`${TYPE_COLORS[item.item_type] || ''} text-xs capitalize`}>{item.item_type}</Badge>
              {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            <p className="text-sm text-muted-foreground">
              Calculated price: <span className="font-semibold text-foreground" data-testid="calculated-price">${item.calculated_price.toFixed(2)}</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Details */}
          <div className="space-y-4 lg:col-span-1">
            {/* Basic Info */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Tag className="w-4 h-4" /> Basic Info</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {/* Image Upload */}
                {canEdit && (
                  <div data-testid="item-image-section">
                    <Label className="text-xs text-muted-foreground">Image <span className="font-normal">(optional)</span></Label>
                    <div className="mt-1.5 flex items-center gap-3">
                      {(item.image_thumb_url || item.image_url) ? (
                        <div className="relative group">
                          <img
                            src={item.image_thumb_url || item.image_url}
                            alt=""
                            className="w-16 h-16 rounded-lg object-cover border border-border/50"
                            data-testid="item-image-preview"
                          />
                          <button
                            type="button"
                            onClick={handleImageDelete}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                            data-testid="item-image-remove"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="w-16 h-16 rounded-lg border-2 border-dashed border-muted-foreground/20 flex flex-col items-center justify-center gap-1 hover:border-primary/30 hover:bg-muted/20 transition-all"
                          data-testid="item-image-upload-btn"
                        >
                          {uploadingImage
                            ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground/40" />
                            : <><Camera className="w-4 h-4 text-muted-foreground/40" /><span className="text-[9px] text-muted-foreground/40">Upload</span></>
                          }
                        </button>
                      )}
                      {(item.image_thumb_url || item.image_url) && (
                        <button type="button" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage} className="text-xs text-primary/70 hover:text-primary transition-colors" data-testid="item-image-change-btn">
                          {uploadingImage ? 'Uploading...' : 'Change'}
                        </button>
                      )}
                      <input ref={imageInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handleImageUpload} data-testid="item-image-input" />
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input defaultValue={item.name} onBlur={e => { if (e.target.value !== item.name) updateField('name', e.target.value); }} data-testid="edit-name" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Textarea defaultValue={item.description || ''} onBlur={e => updateField('description', e.target.value || null)} rows={2} data-testid="edit-description" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">SKU</Label>
                  <Input defaultValue={item.sku || ''} onBlur={e => updateField('sku', e.target.value || null)} placeholder="Optional" data-testid="edit-sku" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Category</Label>
                  <Select value={item.category_id || 'none'} onValueChange={v => updateField('category_id', v === 'none' ? null : v)}>
                    <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Uncategorized</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <Select value={item.item_type} onValueChange={v => updateMultiple({ item_type: v, pricing_mode: v === 'bundle' ? 'bundle' : v === 'labor' ? 'labor' : item.pricing_mode === 'bundle' ? 'fixed' : item.pricing_mode })}>
                    <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="material">Material</SelectItem>
                      <SelectItem value="labor">Labor</SelectItem>
                      <SelectItem value="bundle">Bundle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {item.task_code !== undefined && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Task Code</Label>
                    <Input defaultValue={item.task_code || ''} onBlur={e => updateField('task_code', e.target.value || null)} placeholder="Optional" data-testid="edit-task-code" />
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Unit</Label>
                  <Select value={item.unit || 'each'} onValueChange={v => updateField('unit', v)}>
                    <SelectTrigger data-testid="edit-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="each">Each</SelectItem>
                      <SelectItem value="hour">Hour</SelectItem>
                      <SelectItem value="job">Per Job</SelectItem>
                      <SelectItem value="ft">Foot (ft)</SelectItem>
                      <SelectItem value="sqft">Sq Foot (sqft)</SelectItem>
                      <SelectItem value="lb">Pound (lb)</SelectItem>
                      <SelectItem value="unit">Unit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between py-1">
                  <Label className="text-sm">Taxable</Label>
                  <Switch checked={item.taxable !== false} onCheckedChange={v => updateField('taxable', v)} data-testid="edit-taxable" />
                </div>
                {item.item_type === 'service' && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Default Duration (min)</Label>
                    <Input type="number" min="0" defaultValue={item.default_duration_minutes ?? ''} onBlur={e => updateField('default_duration_minutes', e.target.value ? parseInt(e.target.value) : null)} placeholder="e.g. 60" data-testid="edit-duration" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inventory Consumption */}
            <InventoryModeEditor item={item} onUpdate={setItem} canEdit={canEdit} />
          </div>

          {/* Right: Pricing */}
          <div className="lg:col-span-2 space-y-4">
            {/* Pricing Mode */}
            {!isBundle && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Calculator className="w-4 h-4" /> Pricing</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Pricing Mode</Label>
                    <Select value={item.pricing_mode} onValueChange={v => updateField('pricing_mode', v)}>
                      <SelectTrigger data-testid="edit-pricing-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Price</SelectItem>
                        <SelectItem value="cost_plus_markup">Cost + Markup</SelectItem>
                        <SelectItem value="labor">Labor-based</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {item.pricing_mode === 'fixed' && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Final Price ($)</Label>
                      <Input type="number" step="0.01" min="0" defaultValue={item.final_price} onBlur={e => updateField('final_price', parseFloat(e.target.value) || 0)} data-testid="edit-final-price" />
                    </div>
                  )}

                  {item.pricing_mode === 'cost_plus_markup' && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Base Cost ($)</Label>
                        <Input type="number" step="0.01" min="0" defaultValue={item.base_cost ?? ''} onBlur={e => updateField('base_cost', parseFloat(e.target.value) || 0)} data-testid="edit-base-cost" />
                        {item.inventory_unit_cost != null && (
                          <p className="text-[10px] text-muted-foreground mt-1">Inventory cost: ${item.inventory_unit_cost.toFixed(2)}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Markup Type</Label>
                        <Select value={item.markup_type || 'percent'} onValueChange={v => updateField('markup_type', v)}>
                          <SelectTrigger data-testid="edit-markup-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">Percent (%)</SelectItem>
                            <SelectItem value="multiplier">Multiplier (x)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Markup Value</Label>
                        <Input type="number" step="0.01" defaultValue={item.markup_value ?? ''} onBlur={e => updateField('markup_value', parseFloat(e.target.value) || 0)} data-testid="edit-markup-value" />
                      </div>
                    </div>
                  )}

                  {item.pricing_mode === 'labor' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">Labor Hours</Label>
                          <Input type="number" step="0.25" min="0" defaultValue={item.labor_hours ?? ''} onBlur={e => updateField('labor_hours', parseFloat(e.target.value) || 0)} data-testid="edit-labor-hours" />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Labor Rate ($/hr)</Label>
                          <Input type="number" step="0.01" min="0" defaultValue={item.labor_rate ?? ''} onBlur={e => { const v = parseFloat(e.target.value); updateField('labor_rate', v > 0 ? v : null); }} placeholder={defaultLaborRate != null ? `Default: $${Number(defaultLaborRate).toFixed(2)}` : 'Set rate...'} data-testid="edit-labor-rate" />
                        </div>
                      </div>
                      {/* Effective rate indicator */}
                      <div className="rounded-md bg-muted/50 px-3 py-2 text-xs" data-testid="effective-rate-info">
                        {item.labor_rate_source === 'item' ? (
                          <span className="text-foreground">Using <span className="font-semibold">item rate</span>: ${item.effective_labor_rate?.toFixed(2)}/hr</span>
                        ) : item.labor_rate_source === 'default' ? (
                          <span className="text-foreground">Using <span className="font-semibold">default rate</span>: ${item.effective_labor_rate?.toFixed(2)}/hr</span>
                        ) : (
                          <span className="text-amber-600">No labor rate set. {defaultLaborRate == null ? 'Set a default rate in the Price Book page.' : ''}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Calculated price summary */}
                  <div className="border-t pt-3 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Calculated Price</span>
                    <span className="text-lg font-bold" data-testid="price-summary">${item.calculated_price.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bundle Components */}
            {isBundle && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Bundle Components</CardTitle>
                    {canEdit && (
                      <Button variant="outline" size="sm" onClick={() => setNewComp({ component_type: 'service', component_name: '', quantity: 1, component_unit_price: 0 })} data-testid="add-component-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add Component
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {(item.components || []).length === 0 && !newComp ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Layers className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No components yet</p>
                      <p className="text-xs mt-1">Add services, materials, or labor to this bundle</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="components-table">
                        <thead>
                          <tr className="border-b text-xs text-muted-foreground">
                            <th className="text-left py-2 pr-2">Component</th>
                            <th className="text-right py-2 pr-2 w-16">Qty</th>
                            <th className="text-right py-2 pr-2 w-24">Unit Price</th>
                            <th className="text-right py-2 pr-2 w-24">Total</th>
                            {canEdit && <th className="w-10"></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(item.components || []).map(comp => (
                            <tr key={comp.id} className="border-b last:border-0 group" data-testid={`comp-${comp.id}`}>
                              <td className="py-2.5 pr-2">
                                <Badge variant="outline" className="text-[10px] capitalize mr-1">{comp.component_type}</Badge>
                                <span className="font-medium">{comp.component_name}</span>
                                {comp.component_description && <div className="text-xs text-muted-foreground">{comp.component_description}</div>}
                                {comp.component_item_id && <Badge variant="outline" className="text-[9px] ml-1">Linked</Badge>}
                              </td>
                              <td className="py-2.5 pr-2 text-right">{comp.quantity}</td>
                              <td className="py-2.5 pr-2 text-right">${comp.component_unit_price.toFixed(2)}</td>
                              <td className="py-2.5 pr-2 text-right font-medium">${comp.line_total.toFixed(2)}</td>
                              {canEdit && (
                                <td className="py-2.5">
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100" onClick={() => handleDeleteComponent(comp.id)} data-testid={`delete-comp-${comp.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                          {newComp && (
                            <tr className="border-b bg-muted/30" data-testid="new-comp-row">
                              <td className="py-2 pr-2 space-y-1">
                                <div className="flex gap-2">
                                  <Select value={newComp.component_type} onValueChange={v => setNewComp({ ...newComp, component_type: v })}>
                                    <SelectTrigger className="w-[100px] h-7 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="service">Service</SelectItem>
                                      <SelectItem value="material">Material</SelectItem>
                                      <SelectItem value="labor">Labor</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Input value={newComp.component_name} onChange={e => setNewComp({ ...newComp, component_name: e.target.value })} placeholder="Component name" className="h-7 text-sm flex-1" data-testid="comp-name-input" autoFocus />
                                </div>
                                {/* PB item search for linking */}
                                <div className="relative">
                                  <Input value={compSearchQuery} onChange={e => searchPBItems(e.target.value)} placeholder="Search price book to link..." className="h-7 text-xs" data-testid="comp-search-input" />
                                  {compSearchResults.length > 0 && (
                                    <div className="absolute z-10 top-8 left-0 right-0 bg-background border rounded shadow-lg max-h-40 overflow-y-auto">
                                      {compSearchResults.map(r => (
                                        <button key={r.id} className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted" onClick={() => {
                                          setNewComp({ ...newComp, component_item_id: r.id, component_name: r.name, component_type: r.item_type, component_unit_price: r.calculated_price });
                                          setCompSearchResults([]);
                                          setCompSearchQuery('');
                                        }}>
                                          {r.name} <span className="text-muted-foreground">(${r.calculated_price.toFixed(2)})</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 pr-2"><Input type="number" step="0.01" min="0" value={newComp.quantity} onChange={e => setNewComp({ ...newComp, quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-sm text-right w-16" data-testid="comp-qty-input" /></td>
                              <td className="py-2 pr-2"><Input type="number" step="0.01" min="0" value={newComp.component_unit_price} onChange={e => setNewComp({ ...newComp, component_unit_price: parseFloat(e.target.value) || 0 })} className="h-7 text-sm text-right w-24" data-testid="comp-price-input" /></td>
                              <td className="py-2 pr-2 text-right text-sm font-medium">${((newComp.quantity || 0) * (newComp.component_unit_price || 0)).toFixed(2)}</td>
                              <td className="py-2">
                                <div className="flex gap-0.5">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleAddComponent} data-testid="save-comp-btn"><Save className="w-3.5 h-3.5" /></Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setNewComp(null)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Bundle Total */}
                  <div className="border-t pt-3 mt-3 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Bundle Total</span>
                    <span className="text-lg font-bold" data-testid="bundle-total">${item.calculated_price.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
